import React, { Component, MouseEvent } from "react";
import classnames from "classnames";

import { ConnectionError } from "@formant/core/common/ConnectionError";
import { defined } from "@formant/core/common/defined";
import { narrowError } from "@formant/core/common/narrowError";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { ICommand } from "@formant/core/model/ICommand";
import { ICommandRequestEvent } from "@formant/core/model/ICommandRequestEvent";
import { IEvent } from "@formant/core/model/IEvent";
import { IFilter } from "@formant/core/model/IFilter";
import { Uuid } from "@formant/core/model/Uuid";
import { Button } from "@formant/ui-kit";
import { HStack } from "@formant/ui/component/common/HStack";

import { track } from "../../analytics";
import { cancelCommandEnabled } from "../../config";
import {
    appStore,
    authStore,
    commandStore,
    observeStore,
    userStore,
} from "../../stores";
import { formatDateTime } from "../../utils/formatDateTime";
import { DeviceName } from "../common/DeviceName";
import { EventChip } from "../common/EventChip";
import { EventHeadline } from "../common/EventHeadline";
import { EventIcon } from "../common/EventIcon";
import { FlexSpacer } from "../common/FlexSpacer";
import { Linkify } from "../common/Linkify";
import { RestrictedComponent } from "../common/RestrictedComponent";
import { Tags } from "../common/Tags";
import * as styles from "./EventListItem.scss";

export interface IEventListItemProps {
    event: IEvent;
    filter?: IFilter;
    hideDeviceColumn: boolean;
    minimal?: boolean;
    onSelect?: (event: IEvent) => void;
}

export class EventListItem extends Component<IEventListItemProps> {
    public render() {
        const { event, hideDeviceColumn, minimal } = this.props;
        const { id, time, type, message, viewed, deviceId, tags } = event;
        const { onMouseOver, onMouseOut } = this;

        const showCancelButton =
            cancelCommandEnabled &&
            type === "command-request" &&
            commandStore.isCommandComplete(
                event.deviceId as Uuid,
                (event as ICommandRequestEvent).commandId,
            ) === false;

        const showDetailRow = !hideDeviceColumn || showCancelButton;

        return (
            <div
                key={id}
                className={classnames(
                    styles["event-list-item"],
                    viewed && styles.viewed,
                    minimal && styles.minimal,
                    this.props.onSelect && styles.selectable,
                )}
                onClick={this.onClick}
                onMouseOver={onMouseOver}
                onMouseOut={onMouseOut}
            >
                <EventChip event={event} />
                <EventIcon className={styles.icon} event={event} />
                <div className={styles.body}>
                    <HStack
                        className={styles.message}
                        vAlign="bottom"
                        hAlign="start"
                    >
                        {[
                            "comment",
                            "command-request",
                            "command-delivery",
                            "command-response",
                        ].includes(type || "") && (
                            <div className={styles.headline}>
                                <EventHeadline event={event} />:
                            </div>
                        )}
                        <Linkify text={message} />
                        <FlexSpacer />
                        <div className={styles.time}>
                            {formatDateTime(parseIsoDate(time))}
                        </div>
                    </HStack>
                    {showDetailRow && (
                        <HStack
                            vAlign="center"
                            hAlign="start"
                            className={styles.details}
                        >
                            {deviceId && (
                                <div className={styles.device}>
                                    <DeviceName id={deviceId} size="18" />
                                </div>
                            )}
                            <Tags tags={tags || {}} className={styles.tags} />
                            <FlexSpacer />
                            {showCancelButton && (
                                <RestrictedComponent
                                    resource="events"
                                    requiredAccess="operator"
                                >
                                    <Button
                                        onClickCapture={this.onCancelEvent}
                                        className={styles.action}
                                    >
                                        Cancel
                                    </Button>
                                </RestrictedComponent>
                            )}
                        </HStack>
                    )}
                </div>
            </div>
        );
    }

    private onCancelEvent = async (e: MouseEvent<HTMLButtonElement>) => {
        const { event } = this.props;
        const commandRequestEvent = event as ICommandRequestEvent;
        if (
            commandRequestEvent.type === "command-request" &&
            commandStore.isCommandComplete(
                event.deviceId as Uuid,
                commandRequestEvent.commandId,
            ) === false
        ) {
            const user = userStore.authenticated();
            if (user) {
                const command: ICommand = {
                    organizationId: defined(authStore.organizationId),
                    deviceId: defined(event.deviceId),
                    userId: defined(authStore.userId),
                    command: "formant.cancel_command",
                    parameter: {
                        meta: {
                            id: commandRequestEvent.commandId,
                        },
                        scrubberTime: observeStore.time.toISOString(),
                    },
                };
                try {
                    await commandStore.create(command);
                    const message = "Attempting to cancel command";
                    appStore.setSnackbar(message);
                    track("Cancel command");
                } catch (error) {
                    if (narrowError(error, ConnectionError)) {
                        const message = "Failed to send cancel command";
                        appStore.setSnackbar(message);
                        e.stopPropagation();
                    }
                }
            }
        }
        e.stopPropagation();
    };

    private onClick = (e: MouseEvent) => {
        if (e.target instanceof Element) {
            if (e.target.tagName === "BUTTON") {
                return;
            }
        }
        const { event, onSelect } = this.props;
        if (onSelect) {
            onSelect(event);
        }
        observeStore.hoverEvent = undefined;
    };

    private onMouseOver = () => {
        const { event } = this.props;
        observeStore.hoverEvent = event;
    };

    private onMouseOut = () => {
        observeStore.hoverEvent = undefined;
    };
}
